import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box, Button, CircularProgress, Dialog, Tooltip } from "@mui/material";
import { AttachmentViewer, ConfirmDialog, IconButton } from "@outpave/ds";
import type { AttachmentItem } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Add01Icon,
  Download04Icon,
  PencilEdit01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { formatCashFull } from "utils/formatCash";
import {
  Receipt,
  isReceiptScanFailed,
  isReceiptTransactionDateMissing,
} from "utils/receipts/receiptFormat";
import { useDisplayAttachments } from "hooks/useDisplayAttachments";
import {
  AttachmentSource,
  toAttachmentItems,
} from "utils/attachments/attachmentItems";
import { useAttachmentViewerLabels } from "hooks/useAttachmentLabels";
import { MAX_RECEIPTS_PER_ENTITY } from "utils/receipts/receiptUpload";
import { RECEIPT_FILE_ACCEPT } from "utils/receipts/receiptFileTypes";

const FILMSTRIP_TILE = 56;

interface ReceiptViewerProps {
  open: boolean;
  receipt: Receipt | null;
  onClose: () => void;
  onEdit: () => void;
  onConnect: () => void;
  /**
   * Adding and removing are gated on their handlers rather than on a flag,
   * which is how the package expresses a read-only set. Both surfaces remove;
   * only admin adds.
   */
  onAddAssets?: (files: File[]) => void;
  /** Removing the last image deletes the receipt, which the confirmation says. */
  onRemoveAsset?: (assetId: string) => void;
  /** Blocks mutations while one is in flight. */
  busy?: boolean;
  /** Admin fetches the receipt on open; My Outpave already has it. */
  loading?: boolean;
}

/**
 * The receipt review screen for both admin and My Outpave: the package viewer
 * IS the surface, carrying the receipt's identity and its domain actions in the
 * title and actions slots added in DS 0.7.6.
 */
const ReceiptViewer: React.FC<ReceiptViewerProps> = ({
  open,
  receipt,
  onClose,
  onEdit,
  onConnect,
  onAddAssets,
  onRemoveAsset,
  busy = false,
  loading = false,
}) => {
  const { t } = useTranslation();
  const viewerLabels = useAttachmentViewerLabels();
  const [index, setIndex] = useState(0);
  const [pendingRemoval, setPendingRemoval] = useState<AttachmentItem | null>(
    null,
  );
  const addInputRef = useRef<HTMLInputElement>(null);
  const openedReceiptId = useRef<string | null>(null);

  const assets: AttachmentSource[] = receipt?.assets ?? [];
  const files = toAttachmentItems(assets, t("Receipt"));
  const { downloadAllAssetsAsZip, downloadAsset, printAsset } =
    useDisplayAttachments({ allAttachments: assets });

  useEffect(() => {
    if (open) setIndex(0);
    // Drop a half-answered confirmation rather than showing it on reopen.
    else setPendingRemoval(null);
  }, [open]);

  useEffect(() => {
    if (!open) {
      openedReceiptId.current = null;
      return;
    }
    if (loading || !receipt || openedReceiptId.current === receipt.id) return;
    openedReceiptId.current = receipt.id;
    // Open the editor over the mounted image viewer once per visit. Closing
    // the editor must leave the image open without immediately reopening it.
    if (isReceiptScanFailed(receipt)) onEdit();
  }, [open, loading, receipt, onEdit]);

  // A listed receipt always has an asset, since the server marks a receipt
  // DELETED when its last one goes, so the empty branch is the unreachable one.
  if (open && (loading || !receipt || files.length === 0)) {
    return (
      <Dialog open onClose={onClose} fullScreen>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--color-text-secondary)",
          }}
        >
          {loading || !receipt ? <CircularProgress /> : t("No receipt images")}
        </Box>
      </Dialog>
    );
  }

  // Closed with nothing loaded: no exit animation to play.
  if (!receipt) return null;

  const scanFailed = isReceiptScanFailed(receipt);
  const amount =
    receipt.amount && receipt.amount > 0
      ? formatCashFull(receipt.amount, receipt.currency)
      : null;
  const date = isReceiptTransactionDateMissing(receipt.transactionDate)
    ? null
    : moment(receipt.transactionDate).format("MMMM D, YYYY");
  const identity = [
    receipt.companyName?.trim() || t("No details found"),
    amount,
    date,
  ]
    .filter(Boolean)
    .join(" · ");

  const actions = (
    <>
      {/* The viewer's own Download takes the open image, which is the better
          action for a one-asset receipt. The zip only earns a button when there
          is more than one thing to collect. */}
      {files.length > 1 && (
        <Tooltip title={t("Download all as zip")}>
          <IconButton
            aria-label={t("Download all as zip")}
            onClick={downloadAllAssetsAsZip}
          >
            <HugeiconsIcon icon={Download04Icon} size={20} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t("Edit details")}>
        <IconButton aria-label={t("Edit details")} onClick={onEdit}>
          <HugeiconsIcon icon={PencilEdit01Icon} size={20} />
        </IconButton>
      </Tooltip>
      {/* An empty title renders no tooltip: disableHoverListener would only
          silence the mouse, leaving keyboard focus and touch to announce
          "details missing" over a Connect that works. */}
      <Tooltip title={scanFailed ? t("ReceiptConnectRequiresDetails") : ""}>
        {/* The span keeps the tooltip alive over a disabled button, and takes
            a tab stop only while the button cannot. */}
        <span tabIndex={scanFailed ? 0 : undefined}>
          <Button
            variant="primary-filled"
            onClick={onConnect}
            disabled={scanFailed}
            sx={{ width: "auto" }}
          >
            {t("Connect to transaction")}
          </Button>
        </span>
      </Tooltip>
    </>
  );

  // The viewer owns no upload path, so a set that can grow supplies its own
  // control here. With the grid gone this is the only route to adding.
  const addTile =
    onAddAssets && files.length < MAX_RECEIPTS_PER_ENTITY ? (
      <>
        <Tooltip title={t("Add receipt")}>
          <IconButton
            aria-label={t("Add receipt")}
            disabled={busy}
            onClick={() => addInputRef.current?.click()}
            sx={{ width: FILMSTRIP_TILE, height: FILMSTRIP_TILE }}
          >
            <HugeiconsIcon icon={Add01Icon} size={20} />
          </IconButton>
        </Tooltip>
        <input
          ref={addInputRef}
          type="file"
          hidden
          multiple
          accept={RECEIPT_FILE_ACCEPT}
          onChange={(event) => {
            const picked = Array.from(event.target.files ?? []);
            // Clearing lets the same file be re-picked after a failed upload.
            event.target.value = "";
            if (picked.length > 0) onAddAssets(picked);
          }}
        />
      </>
    ) : undefined;

  const removingLast = files.length === 1;

  return (
    <>
      <AttachmentViewer
        files={files}
        // Null is both "closed" and "which is open", so one value covers both.
        index={open ? Math.min(index, files.length - 1) : null}
        onIndexChange={(next) => (next === null ? onClose() : setIndex(next))}
        title={identity}
        actions={actions}
        // The package names its remove per file; on a receipt the file name is
        // a uuid nobody recognises, so name the thing instead.
        labels={{ ...viewerLabels, remove: () => t("Remove image") }}
        filmstrip={{ tileSize: FILMSTRIP_TILE, trailing: addTile }}
        onDownload={(item) => item.url && downloadAsset(item.url)}
        onPrint={(item) => item.url && printAsset(item.url)}
        // The package fires remove immediately by design and says the
        // confirmation is the host's, so it goes here.
        onRemove={onRemoveAsset ? setPendingRemoval : undefined}
        disabled={busy}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        onCancel={() => {
          if (busy) return;
          setPendingRemoval(null);
        }}
        onConfirm={() => {
          if (pendingRemoval) onRemoveAsset?.(pendingRemoval.id);
          setPendingRemoval(null);
        }}
        title={t("Remove image")}
        message={
          removingLast
            ? t("This is the only image so removing it will delete the receipt")
            : t("Are you sure you want to remove this image?")
        }
        confirmLabel={t("Remove")}
        cancelLabel={t("Cancel")}
        tone="danger"
        busy={busy}
      />
    </>
  );
};

export default ReceiptViewer;
